// src/services/paymentService.js
import api from './api';

/**
 * Create a payment link
 * @param {number} amountUsd - Amount in USD
 * @param {Object} options - Optional parameters
 * @returns {Promise<Object>} - Payment link data
 */
export const createPaymentLink = async (amountUsd, options = {}) => {
  const { email, name, returnUrl } = options;
  
  if (!amountUsd || isNaN(amountUsd) || amountUsd <= 0) {
    throw new Error('Valid amount in USD is required');
  }
  
  try {
    const response = await api.post('/payments/create-link', {
      amount_usd: amountUsd,
      payer_email: email,
      payer_name: name,
      return_url: returnUrl
    });
    
    return response.data;
  } catch (error) {
    console.error('Error creating payment link:', error);
    throw new Error(error.response?.data?.error || 'Failed to create payment link');
  }
};

/**
 * Check payment status
 * @param {string} paymentId - Payment ID
 * @returns {Promise<Object>} - Payment status data
 */
export const checkPaymentStatus = async (paymentId) => {
  try {
    const response = await api.get(`/payments/status/${paymentId}`);
    return response.data;
  } catch (error) {
    console.error('Error checking payment status:', error);
    throw new Error(error.response?.data?.error || 'Failed to check payment status');
  }
};

/**
 * Get pending payments that need approval
 * @returns {Promise<Array>} - Array of pending payments
 */
export const getPendingPayments = async () => {
  try {
    const response = await api.get('/payments/pending');
    return response.data;
  } catch (error) {
    console.error('Error fetching pending payments:', error);
    throw new Error(error.response?.data?.error || 'Failed to fetch pending payments');
  }
};

/**
 * Get approved payments with distributions
 * @returns {Promise<Array>} - Array of approved payments
 */
export const getApprovedPayments = async () => {
  try {
    const response = await api.get('/payments/approved');
    return response.data;
  } catch (error) {
    console.error('Error fetching approved payments:', error);
    throw new Error(error.response?.data?.error || 'Failed to fetch approved payments');
  }
};

/**
 * Approve a payment
 * @param {string} paymentId - Payment ID
 * @returns {Promise<Object>} - Updated payment data
 */
export const approvePayment = async (paymentId) => {
  try {
    const response = await api.post(`/payments/${paymentId}/approve`);
    return response.data;
  } catch (error) {
    console.error('Error approving payment:', error);
    throw new Error(error.response?.data?.error || 'Failed to approve payment');
  }
};

/**
 * Plan distribution of SOL to wallets
 * @param {string} paymentId - Payment ID
 * @param {Array} distributions - Array of distributions with wallet_id and amount_sol
 * @returns {Promise<Object>} - Distribution plan result
 */
export const planDistribution = async (paymentId, distributions) => {
  try {
    const response = await api.post(`/payments/${paymentId}/plan-distribution`, {
      distributions
    });
    return response.data;
  } catch (error) {
    console.error('Error planning distribution:', error);
    throw new Error(error.response?.data?.error || 'Failed to plan distribution');
  }
};

/**
 * Execute a distribution
 * @param {string} distributionId - Distribution ID
 * @returns {Promise<Object>} - Execution result
 */
export const executeDistribution = async (distributionId) => {
  try {
    const response = await api.post(`/payments/distribution/${distributionId}/execute`);
    return response.data;
  } catch (error) {
    console.error('Error executing distribution:', error);
    throw new Error(error.response?.data?.error || 'Failed to execute distribution');
  }
};

/**
 * Create simple checkout page URL
 * @param {number} amountUsd - Amount in USD
 * @param {Object} options - Optional parameters
 * @returns {string} - Checkout page URL
 */
export const createCheckoutUrl = (amountUsd, options = {}) => {
  const { email, name, returnUrl } = options;
  
  // Base URL with the amount
  let url = `${process.env.REACT_APP_API_URL || ''}/pay/${amountUsd}`;
  
  // Add query parameters
  const params = new URLSearchParams();
  if (email) params.append('email', email);
  if (name) params.append('name', name);
  if (returnUrl) params.append('return_url', returnUrl);
  
  // Append query string if there are parameters
  if ([...params].length > 0) {
    url += `?${params.toString()}`;
  }
  
  return url;
};

export default {
  createPaymentLink,
  checkPaymentStatus,
  getPendingPayments,
  getApprovedPayments,
  approvePayment,
  planDistribution,
  executeDistribution,
  createCheckoutUrl
};